import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import { pxToRem } from '../../../utils/pxToRem';
import { conventionContent } from '../content';
import { SectionInner } from '../components/SectionInner';
import { CornerFrame } from '../components/CornerFrame';
import { useDesktop } from '../hooks/useDesktop';

type Person = (typeof conventionContent.coChairs.people)[number];

const quoteTextSx = {
  fontFamily: '"Roboto", sans-serif',
  fontWeight: 400,
  fontSize: { mobile: pxToRem(16), laptop: pxToRem(18) },
  lineHeight: { mobile: pxToRem(20), laptop: pxToRem(24) },
  color: 'primary.main',
  margin: 0,
} as const;

const roleSx = {
  fontFamily: '"Roboto", sans-serif',
  fontWeight: 500,
  fontSize: pxToRem(14),
  lineHeight: pxToRem(20),
  textTransform: 'capitalize',
  margin: 0,
} as const;

function PersonColumn({ person, index }: { person: Person; index: number }) {
  const { readMoreLabel, readLessLabel } = conventionContent.coChairs;
  const [expanded, setExpanded] = useState(false);
  const isDesktop = useDesktop();
  const quoteId = `co-chairs-quote-${index}`;

  return (
    <Stack
      gap={pxToRem(24)}
      sx={{
        width: { mobile: '100%', laptop: pxToRem(352) },
        alignItems: { mobile: 'center', laptop: 'flex-start' },
        textAlign: { mobile: 'center', laptop: 'left' },
        color: 'primary.main',
      }}
    >
      <Stack
        gap={pxToRem(12)}
        sx={{ width: '100%', alignItems: { mobile: 'center', laptop: 'flex-start' } }}
      >
        <Box
          component="img"
          src={person.photoSrc}
          alt={person.photoAlt}
          sx={{
            width: pxToRem(160),
            height: pxToRem(160),
            borderRadius: pxToRem(17),
            objectFit: 'cover',
            display: 'block',
          }}
        />
        <Box sx={{ width: '100%' }}>
          <Typography
            component="p"
            sx={{
              fontFamily: '"Roboto", sans-serif',
              fontWeight: 900,
              fontSize: pxToRem(16),
              lineHeight: pxToRem(20),
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            {person.name}
          </Typography>
          {/* Desktop: single line (roles joined). Mobile: one line per entry (Figma). */}
          <Typography
            component="p"
            sx={{ ...roleSx, whiteSpace: 'nowrap', display: { mobile: 'none', laptop: 'block' } }}
          >
            {person.roleLines.join(' ')}
          </Typography>
          <Box sx={{ display: { mobile: 'block', laptop: 'none' } }}>
            {person.roleLines.map((line, i) => (
              <Typography key={i} component="p" sx={roleSx}>
                {line}
              </Typography>
            ))}
          </Box>
        </Box>
      </Stack>

      <Box
        component="img"
        src={person.signatureSrc}
        alt=""
        aria-hidden="true"
        sx={{ height: pxToRem(40), width: 'auto', objectFit: 'contain', display: 'block' }}
      />

      <Stack
        gap={pxToRem(12)}
        sx={{ width: '100%', alignItems: { mobile: 'center', laptop: 'flex-start' } }}
      >
        {isDesktop ? (
          /* Desktop: full quote, always expanded, no toggle */
          <Stack id={quoteId} gap={pxToRem(24)} sx={{ width: '100%' }}>
            {person.quoteParagraphs.map((paragraph, i) => (
              <Typography key={i} component="p" sx={quoteTextSx}>
                {paragraph}
              </Typography>
            ))}
          </Stack>
        ) : (
          /* Mobile: quote collapses to ~3 lines with an animated toggle */
          <>
            <Collapse in={expanded} collapsedSize={pxToRem(60)} sx={{ width: '100%' }}>
              <Stack id={quoteId} gap={pxToRem(20)} sx={{ width: '100%' }}>
                {person.quoteParagraphs.map((paragraph, i) => (
                  <Typography
                    key={i}
                    component="p"
                    sx={{
                      ...quoteTextSx,
                      ...(!expanded && i === 0
                        ? {
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }
                        : {}),
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Stack>
            </Collapse>

            <Box
              component="button"
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
              aria-controls={quoteId}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: pxToRem(12),
                padding: 0,
                border: 0,
                background: 'none',
                cursor: 'pointer',
                color: 'info.main',
                fontFamily: '"Roboto", sans-serif',
                fontWeight: 400,
                fontSize: pxToRem(16),
                lineHeight: pxToRem(20),
              }}
            >
              {expanded ? readLessLabel : readMoreLabel}
              <Box
                component="svg"
                viewBox="0 0 24 24"
                aria-hidden="true"
                sx={{
                  width: pxToRem(24),
                  height: pxToRem(24),
                  flexShrink: 0,
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transformOrigin: 'center',
                  transition: (theme) =>
                    theme.transitions.create('transform', {
                      duration: theme.transitions.duration.standard,
                      easing: theme.transitions.easing.easeInOut,
                    }),
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              >
                <path
                  d="M6 9l6 6 6-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Box>
            </Box>
          </>
        )}
      </Stack>
    </Stack>
  );
}

export function CoChairs() {
  const { coChairs } = conventionContent;
  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        paddingBlock: { mobile: pxToRem(48), laptop: pxToRem(64) },
      }}
    >
      <SectionInner
        direction={{ mobile: 'column', laptop: 'row' }}
        gap={{ mobile: pxToRem(48), laptop: pxToRem(128) }}
        sx={{ alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            position: 'relative',
            width: pxToRem(256),
            height: pxToRem(146),
            flexShrink: 0,
          }}
        >
          <CornerFrame
            corner="bottom-left"
            inset={{
              mobile: { horizontal: 12.514, vertical: 13.173 },
              laptop: { horizontal: 19, vertical: 20 },
            }}
            radius={{ mobile: 17.783, laptop: 27 }}
          />
          <Typography
            component="h2"
            id={coChairs.headingId}
            variant="h2"
            sx={{
              position: 'absolute',
              top: { mobile: pxToRem(23.5), laptop: pxToRem(28) },
              left: { mobile: '50%', laptop: pxToRem(39) },
              transform: { mobile: 'translateX(-50%)', laptop: 'none' },
              width: { mobile: pxToRem(254), laptop: pxToRem(217) },
              fontSize: pxToRem(32),
              lineHeight: pxToRem(40),
              textAlign: { mobile: 'center', laptop: 'left' },
              margin: 0,
            }}
          >
            {coChairs.headingLines.map((line, i) => (
              <Box component="span" key={i} sx={{ display: 'block' }}>
                {line}
              </Box>
            ))}
          </Typography>
        </Box>

        <Stack
          direction={{ mobile: 'column', laptop: 'row' }}
          gap={{ mobile: pxToRem(56), laptop: pxToRem(32) }}
          sx={{ alignItems: { mobile: 'center', laptop: 'flex-start' } }}
        >
          {coChairs.people.map((person, index) => (
            <PersonColumn key={index} person={person} index={index} />
          ))}
        </Stack>
      </SectionInner>
    </Box>
  );
}
